/* ----------- RESOURCES ----------- */
import { ChangeEvent, useMemo, useRef, useState } from "react";
import { useFetch } from "hooks/useFetch";
import ImageIcon from 'assets/icons/eye-action.svg'

/* ----------- LIBS ----------- */
import moment from "moment";
import { toast } from "react-toastify";

/* ----------- UTILS ----------- */
import { useForm } from "hooks/useForm";
import { useDebounce } from "hooks/useDebounce";

/* ----------- SERVICES ----------- */
import subsidyService from "services/subsidyService";
import companyService from "services/companyService";

/* ----------- COMPONENTS ----------- */
import { Form, Col, Image, Carousel } from "react-bootstrap";
import Actions from "components/admin/Actions";
import DataTable from "components/admin/DataTable";
import Label from "components/admin/Label";
import Input from "components/admin/Input";
import InputDate from "components/admin/InputDate";
import Select from "components/admin/Select";
import loadingDataTable from "components/admin/LoadingDataTable";
import Badge from "components/admin/Badge";
import ButtonIcon from "components/ButtonIcon";
import Modal from "components/admin/Modal";
import Textarea from "components/admin/Textarea";
import Reloading from "components/Reloading";
import ImageWithDimensions from "components/admin/ImageWithDimensions";

/* --------- TYPES ---------- */
import {
    FormFilters,
    ModalApproved,
    ModalDisapproved,
    ModalExported,
    ModalPhotoProps,
    SearchCompany,
    Subsidy,
    ZoomProps,
} from "./types";

/* ---------- STYLES ---------- */
import styles from "pages/admin/subsidies/styles.module.scss";

const initExported = { show: false, all: true, withFilters: false };

let idZoomMoveTimeout: any = null;

const initStateModalPhoto: ModalPhotoProps = {
    show: false,
    photo: {
        name: "",
        urls: [],
    },
};

const initStateModalApproved: ModalApproved = { show: false, comment: "" };
const initStateModalDisapproved: ModalDisapproved = {
    show: false,
    disapproved_reason: "",
    company_id: null,
    id: null,
};

function formatSubsidyValueForInput(value?: string) {
    if (!value) return "";

    return value.toString().replace(".", ",");
}

function isStatusChange(previousStatus?: string) {
    return previousStatus === "approved" || previousStatus === "disapproved";
}

const initStateZoom: ZoomProps = { scale: 1, x: 0, y: 0 };

const status = [
    { key: "pending", item: "Pendente" },
    { key: "approved", item: "Aprovado" },
    { key: "disapproved", item: "Reprovado" },
];

const status_color = {
    pending: "warning",
    approved: "success",
    disapproved: "danger",
};

const breadcrumbs = [
    {
        label: "Inicio",
        path: "/admin/home",
    },
    {
        label: "Gestão de Subsídios",
        path: "/admin/subsidios",
    },
];

const fields = [
    "Usuário",
    "CPF",
    "Empresa",
    "Cartão",
    "Telefone",
    "Data",
    "Fotos",
    "Valor",
    "Valor subsidiado",
    "Status",
    "Ações",
];

const form = {
    page: 1,
    perPage: 10,
    status: null,
    search: "",
    trashed: false,
    created_at: {
        start: null,
        end: null,
    },
};

function formatCPF(cpf: string) {
    return cpf.replaceAll(/(\d{3})(\d{3})(\d{3})(\d{2})/g, "$1.$2.$3-$4");
}

function formatCellphone(cellphone: string | number) {
    cellphone ??= "";
    cellphone = typeof cellphone == "number" ? String(cellphone) : cellphone;

    return cellphone.replaceAll(/(\d{2})(\d{5})(\d{4})/g, "($1) $2-$3");
}

function formatValue(value: string) {
    if (!value) return "";

    return parseFloat(value).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    });
}

function calcPositionEvent(event: any) {
    const rect = event.target.getBoundingClientRect() as DOMRect;
    const x = Math.floor(rect.width / 2 - (event.clientX - rect.left));
    const y = Math.floor(rect.height / 2 - (event.clientY - rect.top));

    return { x, y };
}

function formatFilterDate(date: { start: Date | null; end: Date | null }) {
    if (!date.start && !date.end) return null;

    if (date.start && date.end)
        return `${moment(date.start).format("YYYY-MM-DD")},${moment(date.end).format("YYYY-MM-DD")}`;

    return moment(date.start ?? date.end).format("YYYY-MM-DD");
}

function Status({ subsidy_status }: { subsidy_status: { value: string; label: string } }) {
    return (
        <Badge className={styles.badge_status} color={status_color[subsidy_status.value] ?? "primary"} pill>
            {subsidy_status.label}
        </Badge>
    );
}

export default function Subsidies() {
    const [formFilters, updateFormFilters] = useForm<FormFilters>(form);

    const [isExporting, setIsExporting] = useState(false);
    const [modalExported, setModalExported] = useState<ModalExported>(initExported);

    const [modalApproved, setModalApproved] = useState(initStateModalApproved);
    const [modalDisapproved, setModalDisapproved] = useState(initStateModalDisapproved);

    const [searchCompany, setSearchCompany] = useState<SearchCompany[]>([]);

    const [itemIdSelected, setItemIdSelected] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [modalPhoto, setModalPhoto] = useState(initStateModalPhoto);
    const [isZoom, setIsZoom] = useState(false);
    const [zoomPosition, setZoomPosition] = useState(initStateZoom);

    const imgRef = useRef<HTMLImageElement | null>(null);
    const debounce = useDebounce();
    const debounce2 = useDebounce();
    const debounceClearFilter = useDebounce();

    const { data, error, isValidating, mutate } = useFetch(
        `subsidy?page=${formFilters.page}&perPage=${formFilters.perPage}`,
        {
            params: {
                include: "company,client,client.cards",
                "filter[status]": formFilters.status,
                "filter[search]": formFilters.search,
                "filter[created_at]": formatFilterDate(formFilters.created_at),
                "filter[trashed]": formFilters.trashed,
            },
        }
    );

    const isLoadingSubsidy = !data && !error;
    const isReloadingSubsidy = data && isValidating;
    const isEmptySubsidy = !data || !data?.data || error;

    const class_zoom = isZoom ? styles.zoom_out : styles.zoom_in;

    const requiresApproveComment = isStatusChange(modalApproved.previousStatus);

    const isDisabledApproved =
        !modalApproved.id ||
        !modalApproved.company_id ||
        !modalApproved.value ||
        (modalApproved.previousStatus === "pending" && !modalApproved.product_value) ||
        (requiresApproveComment && !modalApproved.comment?.trim());

    const isDisabledDisapproved =
        !modalDisapproved.id ||
        !modalDisapproved.company_id ||
        !modalDisapproved.disapproved_reason?.trim();

    const filters = [
        <Form.Group className="col-11 col-lg-4 col-md-6 p-0 mx-auto mx-md-0" key="filter-search">
            <Label text="Pesquisar" />

            <Input
                type="search"
                iconRight="search"
                placeholder="Buscar por cliente, empresa, cartão ou CPF"
                aria-label="Digite o CPF"
                value={formFilters.search}
                onChange={handleFilterSearch}
            />
        </Form.Group>,

        <Form.Group className="row col-11 col-lg-4 col-md-6 p-0 mx-auto mx-md-0" key="filter-date">
            <Label text="Data de criação" />

            <Col className="col-6 p-0">
                <InputDate
                    className={styles.input_datetime_left}
                    placeholder="Data inicial"
                    selectsStart
                    iconLeft="calendar"
                    selected={formFilters.created_at.start}
                    dateFormat="dd/MM/yyyy"
                    startDate={formFilters.created_at.start}
                    endDate={formFilters.created_at.end}
                    onChange={(date) => handleFilterCreatedAt(date, "start")}
                />
            </Col>

            <Col className="col-6 p-0">
                <InputDate
                    className={styles.input_datetime_right}
                    placeholder="Data final"
                    selectsEnd
                    style={{ borderLeft: "none !important" }}
                    iconLeft="calendar"
                    selected={formFilters.created_at.end}
                    dateFormat="dd/MM/yyyy"
                    startDate={formFilters.created_at.start}
                    endDate={formFilters.created_at.end}
                    minDate={formFilters.created_at.start}
                    onChange={(date) => handleFilterCreatedAt(date, "end")}
                />
            </Col>
        </Form.Group>,

        <Form.Group className="col-11 col-lg-3 col-md-5 p-0 mx-auto mx-md-0" key="filter-status">
            <Label text="Status" />

            <Select
                placeholder="Selecione o status"
                items={status}
                value={formFilters.status}
                onChange={(key) => updateFormFilters("status", key)}
            />
        </Form.Group>,

        <Form.Group className="col-11 col-lg-3 col-md-5 p-0 mx-auto mx-md-0" key="filter-withTrashed">
            <Form.Check
                type="checkbox"
                label="Somente subsídio excluído"
                checked={formFilters.trashed}
                onChange={handleFilterTrashed}
            />
        </Form.Group>,
    ];

    /* --- FILTERS --- */

    function handleFilterSearch(event: ChangeEvent<HTMLInputElement>) {
        const search = event.target.value;
        updateFormFilters("search", search);

        debounce(() => {
            updateFormFilters("page", 1);
            mutate();
        }, 500)();
    }

    function handleFilterCreatedAt(date: Date | null, field: string) {
        updateFormFilters("created_at", {
            ...formFilters.created_at,
            [field]: date,
        });
    }

    function handleFilterTrashed(event: ChangeEvent<HTMLInputElement>) {
        const checked = event.target.checked;

        updateFormFilters("trashed", checked);
    }

    function handleClearFilters() {
        updateFormFilters(
            ["search", "status", "trashed", "page", "created_at"],
            ["", null, false, 1, { start: null, end: null }]
        );

        debounceClearFilter(() => mutate(), 20)();
    }

    function handleSearchCompany(event) {
        if (event.target.value.length < 3) return;

        companyService
            .list({ params: { "filter[name]": event.target.value } })
            .then(({ data }) => {
                const company = data?.data?.map(({ id, name }: SearchCompany) => ({
                    id,
                    name,
                }));

                setSearchCompany(company);
            })
            .catch((err) => {
                if (err.status === 404) toast.error("Nenhuma empresa encontrada!");

                toast.error("Não foi possível buscar as empresas, tente mais tarde!");
            });
    }

    /* --- MODAIS APPROVED OU DISPROVED --- */
    function handleApproved() {
        if (!modalApproved.company_id) return toast.warn("Selecione uma empresa!");

        const payload: Parameters<typeof subsidyService.update>[1] = {
            status: "approved",
            approved_at: moment().toISOString(),
            disapproved_at: null,
            disapproved_reason: null,
            company_id: modalApproved.company_id,
            value_approved: modalApproved.value.replace(".", "").replace(",", "."),
        };

        if (modalApproved.comment?.trim()) {
            payload.approved_reason = modalApproved.comment.trim();
        }

        subsidyService
            .update(modalApproved.id, payload)
            .then(() => {
                toast.success(
                    modalApproved.previousStatus === "disapproved"
                        ? "Status alterado para aprovado!"
                        : "Subsídio aprovado!"
                );

                setModalApproved(initStateModalApproved);

                mutate();
            })
            .catch(() => toast.error("Não foi possível aprovar o subsídio!"));
    }

    function handleDisapproved() {
        if (!modalDisapproved.company_id) return toast.warn("Selecione uma empresa!");

        subsidyService
            .update(modalDisapproved.id, {
                status: "disapproved",
                disapproved_reason: modalDisapproved.disapproved_reason?.trim(),
                disapproved_at: moment().toISOString(),
                approved_at: null,
                approved_reason: null,
                company_id: modalDisapproved.company_id,
            })
            .then(() => {
                toast.success(
                    modalDisapproved.previousStatus === "approved"
                        ? "Status alterado para reprovado!"
                        : "Subsídio reprovado!"
                );

                setModalDisapproved(initStateModalDisapproved);

                mutate();
            })
            .catch(() => toast.error("Não foi possível reprovado o subsídio!"));
    }

    function handleSelectCompany(company_id: number, type: "approved" | "disapproved") {
        const type_action = {
            approved: () =>
                setModalApproved({
                    ...modalApproved,
                    company_id,
                }),
            disapproved: () =>
                setModalDisapproved({
                    ...modalDisapproved,
                    company_id,
                }),
        };

        type_action[type]();
    }

    function handleValue(value: string) {
        value = value.replace(/[^\d.,]/g, "");

        setModalApproved({
            ...modalApproved,
            value: value,
        });
    }

    function handleProductValue(product_value: string) {
        product_value = product_value.replace(/[^\d.,]/g, "");

        setModalApproved({
            ...modalApproved,
            product_value: product_value,
        });
    }

    function handleDisapprovedReason(disapproved_reason: string) {
        if (disapproved_reason.length > 254) return;

        setModalDisapproved({
            ...modalDisapproved,
            disapproved_reason,
        });
    }

    function handleApprovedComment(comment: string) {
        if (comment.length > 254) return;

        setModalApproved({
            ...modalApproved,
            comment,
        });
    }

    /*  --- MODAL PHOTO --- */
    function handleHiddenDetailsPhoto() {
        setModalPhoto({ show: false, photo: { name: null, urls: [] } });

        setIsZoom(false);
        setZoomPosition({ scale: 1, x: 0, y: 0 });
    }

    function handleZoomableImage(event: any) {
        const { x, y } = calcPositionEvent(event);

        setZoomPosition(isZoom ? { scale: 1, x: 0, y: 0 } : { scale: 1.5, x, y });

        setIsZoom((z) => !z);
    }

    function handleMousemove(event: any) {
        if (!isZoom) return;

        idZoomMoveTimeout = setTimeout(() => {
            idZoomMoveTimeout && clearTimeout(idZoomMoveTimeout);

            if (event.pageY < 100 || event.pageY > 850) return;

            const y = Math.floor(event.target.clientHeight - event.pageY + 10);
            setZoomPosition((e) => ({ ...e, y }));
        }, 15);
    }

    function handleDeleteItem() {
        if (!itemIdSelected) return;

        setIsDeleting(true);

        subsidyService
            .delete(itemIdSelected)
            .then(() => {
                data.data = data.data?.filter((x: Subsidy) => x.id !== itemIdSelected);

                mutate(data.data);

                setIsDeleting(false);
                setItemIdSelected(null);

                toast.success("Subsídio excluído!");
            })
            .catch(() => toast.error("Não foi possível delete esse subsidio"));
    }

    /* --- EXPORT TO EXCEL --- */
    function exportToExcel() {
        setIsExporting(true);

        subsidyService
            .export({
                params: {
                    page: formFilters.page,
                    perPage: formFilters.perPage,
                    include: "company,client,client.cards",
                    "filter[status]": formFilters.status,
                    "filter[search]": formFilters.search,
                    "filter[created_at]": formatFilterDate(formFilters.created_at),
                    all: modalExported.all,
                    withFilters: modalExported.withFilters,
                },
            })
            .then(({ data }) => data.subsidies_excel && window.open(data.subsidies_excel, "_self"))
            .finally(() => setIsExporting(false))
            .catch(() => toast.error("Ocorreu um erro ao exportar"));

        setModalExported(initExported);
    }

    const listSubsidies = useMemo(() => {
        function definePropsModal(company_id: number | null) {
            company_id &&
                companyService.get(company_id).then(({ data: company }) => {
                    setSearchCompany([company]);
                });
        }

        function handleShowModalApproved(subsidy: Subsidy) {
            definePropsModal(subsidy.company?.id);

            setModalApproved({
                show: true,
                id: subsidy.id,
                company_id: subsidy.company?.id,
                value: formatSubsidyValueForInput(subsidy.value_approved ?? subsidy.value),
                previousStatus: subsidy.status.value,
                comment: "",
            });
        }

        function handleShowModalDisapproved(subsidy: Subsidy) {
            definePropsModal(subsidy.company?.id);

            setModalDisapproved({
                show: true,
                id: subsidy.id,
                company_id: subsidy.company?.id,
                disapproved_reason: "",
                previousStatus: subsidy.status.value,
            });
        }

        function handleShowDetailsPhoto(photo: { name: string; urls: string[] }) {
            setModalPhoto({ show: true, photo });
        }

        return data?.data?.map((subsidy: Subsidy) => (
            <tr key={`subsidy-${subsidy.id}`} data-id={subsidy.id}>
                <td>{subsidy.client.name}</td>
                <td>{formatCPF(subsidy.client.cpf)}</td>
                <td>{subsidy.company?.name}</td>
                <td>
                    {subsidy.telenet_card_label && (
                        <Col className="d-flex flex-wrap justify-content-center">
                            <Badge color="primary" key={`card-badge-${subsidy.telenet_card_id}`} pill>
                                {subsidy.telenet_card_label}
                            </Badge>
                        </Col>
                    )}
                </td>
                <td>{formatCellphone(subsidy.client.cellphone)}</td>
                <td>{moment(subsidy.created_at).format("DD/MM/YYYY")}</td>
                <td>
                    <Col className="d-flex flex-column flex-wrap justify-content-center">
                        <Col
                            className="col-auto"
                            onClick={() =>
                                handleShowDetailsPhoto({
                                    name: "Documento Fiscal",
                                    urls: subsidy.fiscal_document_url,
                                })
                            }
                        >
                            <div className={styles.botao_fiscal}>
                              <div>
                                <img src={ImageIcon} alt=""/>
                              </div>
                              <div className={styles.botao_fiscal_texto}>Documento Fiscal</div>
                              <div className={styles.tooltip_info}>
                                  <ImageWithDimensions urlImage={JSON.stringify(subsidy.fiscal_document_url)} />     
                              </div>
                            </div>
                        </Col>

                        <Col
                            className="col-auto"
                            onClick={() =>
                                handleShowDetailsPhoto({
                                    name: "Documento",
                                    urls: subsidy.document_url,
                                })
                            }
                        >
                            <div className={styles.botao_fiscal}>
                              <div>
                                <img src={ImageIcon} alt=""/>
                              </div>
                              <div className={styles.botao_fiscal_texto}>Documento</div>
                              <div className={styles.tooltip_info}>
                                  <ImageWithDimensions urlImage={JSON.stringify(subsidy.document_url)} />                                  
                              </div>                              
                            </div>
                        </Col>
                    </Col>
                </td>
                <td> {formatValue(subsidy.value)}</td>
                <td> {formatValue(subsidy.value_approved ?? "")}</td>
                <td>
                    <Status subsidy_status={subsidy.status} />
                </td>
                <td>
                    <Col className={styles.buttons_actions}>
                        {(subsidy.status.value === "pending" || subsidy.status.value === "disapproved") && (
                            <ButtonIcon
                                className={styles.button_action_approved}
                                onClick={() => handleShowModalApproved(subsidy)}
                            >
                                {subsidy.status.value === "disapproved" ? "Alterar para Aprovado" : "Aprovar"}
                            </ButtonIcon>
                        )}

                        {(subsidy.status.value === "pending" || subsidy.status.value === "approved") && (
                            <ButtonIcon
                                className={styles.button_action_disapproved}
                                onClick={() => handleShowModalDisapproved(subsidy)}
                            >
                                {subsidy.status.value === "approved" ? "Alterar para Reprovado" : "Reprovar"}
                            </ButtonIcon>
                        )}
                    </Col>
                </td>
            </tr>
        ));
    }, [data]);

    return (
        <>
            <Actions
                title="Gestão de Subsídios"
                breadcrumb={breadcrumbs}
                actionButtons={
                    <>
                        {itemIdSelected && (
                            <Col className="col-auto col-xl-3 d-grid">
                                <ButtonIcon
                                    icon={isDeleting ? "loading" : "trash-action"}
                                    disabled={isDeleting}
                                    onClick={handleDeleteItem}
                                >
                                    {isDeleting ? "Excluindo" : "Excluir Subsídio"}
                                </ButtonIcon>
                            </Col>
                        )}
                        <Col className="col-auto d-grid">
                            <ButtonIcon
                                className="px-3"
                                icon={isExporting ? "loading" : "excel-action"}
                                disabled={isExporting}
                                onClick={() => setModalExported((e) => ({ ...e, show: true }))}
                            >
                                {isExporting ? "Exportando" : "Exportar para Excel"}
                            </ButtonIcon>
                        </Col>
                    </>
                }
            />

            <DataTable
                filters={filters}
                fields={fields}
                currentPage={formFilters.page}
                perPage={formFilters.perPage}
                total={data?.total}
                onSearch={() => mutate()}
                onPaginate={(p) => updateFormFilters("page", p)}
                onPerPage={(p) => updateFormFilters("perPage", p)}
                onSelectLine={(id) => setItemIdSelected(id)}
                onClearFilters={handleClearFilters}
                data={
                    isLoadingSubsidy
                        ? loadingDataTable(fields.length, formFilters.perPage)
                        : isEmptySubsidy
                            ? []
                            : listSubsidies
                }
            />

            {/* MODAL CONFIRM EXPORT */}
            <Modal
                show={modalExported.show}
                title="Exportar Subsídios"
                buttons={[<ButtonIcon onClick={exportToExcel}>Confirmar</ButtonIcon>]}
                onClose={() => setModalExported(initExported)}
            >
                <Col className="col-12 h-100">
                    <Form>
                        <Form.Check
                            type="switch"
                            className="cursor-pointer"
                            label="Exportar todos os subsídios"
                            checked={modalExported.all}
                            onChange={() => setModalExported((e) => ({ ...e, all: !e.all }))}
                        />

                        <Form.Check
                            type="switch"
                            className="cursor-pointer"
                            label="Exportar com filtro"
                            checked={modalExported.withFilters}
                            onChange={() => setModalExported((e) => ({ ...e, withFilters: !e.withFilters }))}
                        />
                    </Form>
                </Col>
            </Modal>
            {/* -------------------- */}

            {/* MODAL PHOTO */}
            <Modal
                ref={imgRef}
                show={modalPhoto.photo.urls.length > 0}
                contentClassName={styles.modal_content_photo}
                modalBodyClassName={styles.modal_photo_body}
                title={modalPhoto.photo?.name}
                buttonVariant="white"
                onClose={handleHiddenDetailsPhoto}
                fullscreen
                keyboard
            >
                <Carousel className="col-12" style={{ height: "95%" }} interval={null} touch>
                    {modalPhoto.photo.urls.map((url) => (
                        <Carousel.Item key={url} className="col-12" style={{ height: "95vh" }}>
                            <Col className="col-12 h-100 d-flex justify-content-center align-items-center">
                                <Col className={`${styles.container_modal_photo} ${class_zoom} mx-auto`}>
                                    <Image
                                        style={{
                                            transform: `scale(${zoomPosition.scale}) translate(${zoomPosition.x}px, ${zoomPosition.y}px)`,
                                        }}
                                        src={url}
                                        alt={modalPhoto.photo?.name}
                                        title="Click  para ampliar a imagem"
                                        fluid
                                        onClick={handleZoomableImage}
                                        onMouseMove={handleMousemove}
                                    />
                                </Col>
                            </Col>
                        </Carousel.Item>
                    ))}
                </Carousel>
            </Modal>
            {/* ---------------- */}

            {/* MODAL APPROVED */}
            <Modal
                show={modalApproved.show}
                title={
                    modalApproved.previousStatus === "disapproved"
                        ? "Alterar status para Aprovado"
                        : "Aprovar solicitação do Subsídio"
                }
                buttons={[
                    <ButtonIcon
                        className={styles.button_modal_cancel}
                        onClick={() => setModalApproved(initStateModalApproved)}
                    >
                        Cancelar
                    </ButtonIcon>,

                    <ButtonIcon
                        className={styles.button_modal_approved}
                        disabled={isDisabledApproved}
                        onClick={handleApproved}
                    >
                        {modalApproved.previousStatus === "disapproved" ? "Confirmar alteração" : "Aprovar"}
                    </ButtonIcon>,
                ]}
            >
                <Form className="mb-3" onSubmit={(e) => e.preventDefault()}>
                    {requiresApproveComment && (
                        <Form.Group className="mb-3">
                            <Label text="Comentário da alteração" className={styles.label_modal} />

                            <Textarea
                                placeholder="Escreva aqui o motivo da alteração de status"
                                value={modalApproved.comment}
                                onChange={(e) => handleApprovedComment(e.currentTarget.value)}
                                style={{ height: "120px" }}
                            />

                            <p className={styles.text_info_title}>{`caracteres ${modalApproved.comment?.length ?? 0}/254`}</p>
                        </Form.Group>
                    )}

                    <Form.Group>
                        <Label text="Valor do subsídio"></Label>

                        <Input
                            placeholder="valor do subsídio"
                            value={modalApproved.value}
                            onChange={(e) => handleValue(e.currentTarget.value)}
                        />
                    </Form.Group>

                    {modalApproved.previousStatus === "pending" && (
                        <Form.Group>
                            <Label text="Valor do produto"></Label>

                            <Input
                                placeholder="valor do produto"
                                value={modalApproved.product_value}
                                onChange={(e) => handleProductValue(e.currentTarget.value)}
                            />
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                        <Label text="Selecione uma empresa" />

                        <Input
                            iconRight="search"
                            placeholder="Pesquise aqui uma empresa"
                            onKeyUp={debounce2(handleSearchCompany, 500)}
                        />
                    </Form.Group>

                    <Col className={`col-12 ${styles.box_show_search} rounded`}>
                        {searchCompany.map(({ id, name }) => (
                            <Col
                                key={`option-establishment-approved-${id}`}
                                className={`d-flex align-items-start py-3 px-4 cursor-pointer ${modalApproved.company_id === id ? styles.box_item_selected : ""
                                    }`}
                                onClick={() => handleSelectCompany(id, "approved")}
                            >
                                {name}
                            </Col>
                        ))}
                    </Col>
                </Form>
            </Modal>
            {/* ------------- */}

            {/* MODAL DISAPPROVED */}
            <Modal
                show={modalDisapproved.show}
                title={
                    modalDisapproved.previousStatus === "approved"
                        ? "Alterar status para Reprovado"
                        : "Reprovar solicitação do subsídio"
                }
                buttons={[
                    <ButtonIcon
                        className={styles.button_modal_cancel}
                        onClick={() => setModalDisapproved(initStateModalDisapproved)}
                    >
                        Cancelar
                    </ButtonIcon>,

                    <ButtonIcon
                        className={styles.button_modal_disapproved}
                        onClick={handleDisapproved}
                        disabled={isDisabledDisapproved}
                    >
                        {modalDisapproved.previousStatus === "approved" ? "Confirmar alteração" : "Reprovar"}
                    </ButtonIcon>,
                ]}
            >
                <Form className="mb-3" onSubmit={(e) => e.preventDefault()}>
                    <Form.Group className="mb-3">
                        <Label
                            text={
                                modalDisapproved.previousStatus === "approved"
                                    ? "Comentário da alteração"
                                    : "Motivo da reprovação"
                            }
                            className={styles.label_modal}
                        />

                        <Textarea
                            placeholder="Escreva aqui o motivo da reprovação"
                            value={modalDisapproved?.disapproved_reason}
                            onChange={(e) => handleDisapprovedReason(e.currentTarget.value)}
                            style={{ height: "120px" }}
                        />

                        <p className={styles.text_info_title}>{`caracteres ${modalDisapproved?.disapproved_reason?.length ?? 0
                            }/254`}</p>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Label text="Selecione uma empresa" />

                        <Input
                            iconRight="search"
                            placeholder="Pesquise aqui uma empresa"
                            onKeyUp={debounce2(handleSearchCompany, 500)}
                        />
                    </Form.Group>

                    <Col className={`col-12 ${styles.box_show_search} rounded`}>
                        {searchCompany.map(({ id, name }) => (
                            <Col
                                key={`option-establishment-disapproved-${id}`}
                                className={`d-flex align-items-start py-3 px-4 cursor-pointer ${modalDisapproved.company_id === id ? styles.box_item_selected : ""
                                    }`}
                                onClick={() => handleSelectCompany(id, "disapproved")}
                            >
                                {name}
                            </Col>
                        ))}
                    </Col>
                </Form>
            </Modal>
            {/* ----------------- */}

            {/* RELOADING SPINNER */}
            {isReloadingSubsidy && <Reloading />}
            {/* ----------------- */}
        </>
    );
}
